import { createCashIn, createCashInVIPPoints } from "apis/cash-in.api";
import { getLoyaltyPoints } from "apis/vip-biz.api";
import { getWallet } from "apis/wallet.api";
import { Button, Image, Intro, Modal, Text, Title } from "components/commons";
import { FleetContext, UserContext } from "contexts";
import { CashInChannel, FleetStatus, Path, UserAccess } from "enums";
import VIPRedemptionMethod from "enums/vip-redemption-method";
import { useApi, useModal, useMount } from "hooks";
import { History, PointsRedeemed, PointsRedemptionFailed } from "images";
import locale from "localization";
import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { formatNumber } from "utils";
import styles from "./redeem-points.module.scss";

const DENOMINATIONS = [5000, 10000, 25000, 50000, 100000];

const RedeemPointsModule = () => {
  const history = useHistory();
  const { fleet } = useContext(FleetContext);
  const { userAccess } = useContext(UserContext);
  const { fleetId, status } = fleet || {};

  const getUserAccess = userAccess.find((item) => {
    return item.key === UserAccess.VIPRedeemPoints;
  });
  const hasModifyAccess = getUserAccess?.fullAccess || getUserAccess?.modifyAccess;
  const deactivated = status === FleetStatus.Deactivated;

  const [denomination, setDenomination] = useState(null);
  const [method, setMethod] = useState(null);
  const resultModal = useModal();

  const {
    request: getLoyaltyPointsRequest,
    loading: fetchingLoyaltyPoints,
    result: pointsBalance = {},
  } = useApi({
    api: getLoyaltyPoints,
    params: { fleetId },
    modalError: false,
  });

  const { request: getWalletBalanceRequest, result: wallet = {} } = useApi({
    api: getWallet,
    params: { fleetId },
  });

  const { request: createLOCQPayRedemptionRequest, loading: creatingLOCQPayRedemption } = useApi({
    api: createCashIn,
    modalError: false,
  });

  const { request: createCashRedemptionRequest, loading: creatingCashRedemption } = useApi({
    api: createCashInVIPPoints,
    modalError: false,
  });

  const { points = 0 } = pointsBalance;
  const { walletBalance = 0 } = wallet;
  const redeeming = creatingLOCQPayRedemption || creatingCashRedemption;
  const canContinue =
    !!denomination &&
    !!method &&
    denomination <= points &&
    !!hasModifyAccess &&
    !deactivated &&
    !fetchingLoyaltyPoints;

  useMount(() => {
    getLoyaltyPointsRequest();
    getWalletBalanceRequest();
  });

  const redeem = async () => {
    try {
      let referenceId;
      if (method === VIPRedemptionMethod.LOCQPay) {
        const res = await createLOCQPayRedemptionRequest({
          amount: denomination,
          method: CashInChannel.VIP,
          paymentProcessor: "vip",
          fleetId,
        });
        referenceId = res?.cashinId;
      } else {
        const res = await createCashRedemptionRequest({
          points: denomination,
          type: "fleet",
          rewardRedeemed: VIPRedemptionMethod.Cash,
          fleetId,
        });
        referenceId = res?.conversionHistory?.pointsHistoryId;
      }

      resultModal.show({ success: true, points: denomination, referenceId });
      setDenomination(null);
      setMethod(null);
      getLoyaltyPointsRequest();
      getWalletBalanceRequest();
    } catch (err) {
      resultModal.show({ success: false });
    }
  };

  const closeResultModal = () => {
    const { success } = resultModal;
    resultModal.close();
    if (success) {
      history.push(Path.VIPRedemptionHistory);
    }
  };

  return (
    <div>
      <Modal
        {...resultModal}
        close={closeResultModal}
        className={styles.modal}
        headerClassName={styles.modalHeader}
        contentClassName={styles.modalContent}
      >
        <Image
          src={resultModal.success ? PointsRedeemed : PointsRedemptionFailed}
          className={styles.modalImage}
        />
        <Title small className={styles.modalTitle}>
          {resultModal.success ? locale.pointsRedeemed : locale.pointsRedemptionFailed}
        </Title>
        <Text className={styles.modalText}>
          {resultModal.success ? (
            <locale.Populate
              text={locale.pointsRedeemedContent}
              items={[formatNumber(resultModal.points, 0), <b>{resultModal.referenceId}</b>]}
            />
          ) : (
            locale.pointsRedemptionFailedContent
          )}
        </Text>
        <Button primary className={styles.modalButton} onClick={closeResultModal}>
          {locale.gotIt}
        </Button>
      </Modal>

      <Intro
        title={locale.redeemPoints}
        actionSecondaryText={locale.redemptionHistory}
        actionSecondaryClassName={styles.historyButton}
        actionSecondaryStartIcon={<Image src={History} />}
        actionSecondaryOnClick={() => history.push(Path.VIPRedemptionHistory)}
      />

      <div className={styles.section}>
        <Text label>{locale.vipPointsBalance}</Text>
        <Text strong bolder className={styles.balance}>
          {locale.populate(locale.nPoints, [formatNumber(points, 0)])}
        </Text>
      </div>

      <div className={styles.section}>
        <Text label>{locale.selectDenomination}</Text>
        <div className={styles.options}>
          {DENOMINATIONS.map((value) => (
            <div
              key={value}
              className={`${styles.denomination} ${denomination === value ? styles.selected : ""}`}
              onClick={() => setDenomination(value)}
            >
              ₱{formatNumber(value, 0)}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <Text label>{locale.selectRedemptionMethod}</Text>
        <div className={styles.options}>
          <div
            className={`${styles.method} ${styles.locqpay} ${
              method === VIPRedemptionMethod.LOCQPay ? styles.selected : ""
            }`}
            onClick={() => setMethod(VIPRedemptionMethod.LOCQPay)}
          >
            <span className={styles.peso}>₱</span>
            <div>
              <Text strong bolder className={styles.methodTitle}>
                {locale.locqpayBalance}
              </Text>
              <Text className={styles.methodSubtitle}>{formatNumber(walletBalance, 2)} PHP</Text>
            </div>
          </div>
          <div
            className={`${styles.method} ${styles.cash} ${
              method === VIPRedemptionMethod.Cash ? styles.selected : ""
            }`}
            onClick={() => setMethod(VIPRedemptionMethod.Cash)}
          >
            {locale.cash}
          </div>
        </div>
      </div>

      <Button
        primary
        className={styles.continue}
        disabled={!canContinue}
        loading={redeeming}
        onClick={redeem}
      >
        {locale.continue}
      </Button>
    </div>
  );
};

export default RedeemPointsModule;
